interface OpenAIWorksheetRequest {
  type: 'puzzle' | 'matching' | 'sorting';
  grade: string;
  subject: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

interface OpenAIWorksheetResponse {
  content: any;
  metadata: {
    estimatedTime: number;
    skillsTargeted: string[];
  };
}

export const generateOpenAIWorksheet = async (request: OpenAIWorksheetRequest): Promise<OpenAIWorksheetResponse> => {
  console.log('🤖 Generating AI worksheet with OpenAI for:', request);

  try {
    const prompt = generateDynamicWorksheetPrompt(request);

    // Lazy import to keep this module tree-shakeable on the server.
    const { invokeAI } = await import('@/lib/aiInvoke');
    const { data: result, error } = await invokeAI<OpenAIWorksheetResponse>(
      'generate-ai-worksheet',
      {
        body: {
          prompt,
          type: request.type,
          grade: request.grade,
          subject: request.subject,
          difficulty: request.difficulty,
        },
      },
    );

    if (error || !result) {
      throw new Error(error?.message ?? 'AI generation failed');
    }
    console.log('✅ OpenAI worksheet generated successfully');
    
    return {
      content: {
        ...(result.content as object),
        generatedAt: new Date().toISOString(),
        aiGenerated: true,
      },
      metadata: result.metadata,
    };
  } catch (error) {
    console.error('❌ OpenAI generation failed, using enhanced fallback:', error);
    return generateEnhancedFallback(request);
  }
};

const generateDynamicWorksheetPrompt = (request: OpenAIWorksheetRequest): string => {
  const { type, grade, subject, difficulty } = request;
  const timestamp = Date.now();
  const randomSeed = Math.floor(Math.random() * 1000);
  
  // Get random topic variations for the subject
  const topicVariations = getRandomTopicVariations(subject, parseInt(grade));
  const selectedTopic = topicVariations[randomSeed % topicVariations.length];
  
  // Get random instruction variations
  const instructionVariations = getInstructionVariations(type);
  const selectedInstruction = instructionVariations[randomSeed % instructionVariations.length];
  
  // Get random creativity prompts
  const creativityPrompts = getCreativityPrompts();
  const selectedCreativity = creativityPrompts[timestamp % creativityPrompts.length];
  
  const basePrompts = {
    puzzle: `Create a ${difficulty} word search puzzle for Grade ${grade} ${subject} focusing on ${selectedTopic}. ${selectedCreativity} Generate 8-12 age-appropriate vocabulary words related to ${selectedTopic} that match Grade ${grade} curriculum. Include educational hints for each word. ${selectedInstruction} Make it unique and engaging for ${grade} graders. Seed: ${randomSeed}. Return as JSON with: {type: "wordsearch", title: "...", instructions: "...", items: [{word: "WORD", found: false, hint: "..."}]}`,
    
    matching: `Create a ${difficulty} matching activity for Grade ${grade} ${subject} focusing on ${selectedTopic}. ${selectedCreativity} Generate 6-8 items and 2-3 categories for students to match correctly. ${selectedInstruction} Make it educational and age-appropriate. Seed: ${randomSeed}. Return as JSON with: {type: "matching", title: "...", instructions: "...", items: ["item1", "item2", "item3"], categories: [{"name": "Category 1", "correctItems": ["item1", "item2"]}, {"name": "Category 2", "correctItems": ["item3"]}]}`,
    
    sorting: `Create a ${difficulty} sorting activity for Grade ${grade} ${subject} focusing on ${selectedTopic}. ${selectedCreativity} Generate 2-3 creative categories with 4-6 items each that Grade ${grade} students need to organize by ${selectedTopic} concepts. ${selectedInstruction} Make it educational and thought-provoking. Seed: ${randomSeed}. Return as JSON with: {type: "sorting", title: "...", instructions: "...", items: [{name: "Category Name", items: [{name: "item", placed: false}]}]}`
  };

  const enhancedPrompt = basePrompts[type] + ` 
  
  IMPORTANT CREATIVITY REQUIREMENTS:
  - Use varied vocabulary and terminology each time
  - Include different aspects of ${selectedTopic} 
  - Make the content unique and avoid repetitive patterns
  - Add educational variety while maintaining Grade ${grade} appropriateness
  - Focus specifically on ${selectedTopic} within ${subject}
  - Create content that feels fresh and engaging
  - Vary the difficulty within the ${difficulty} level
  - Use different educational approaches and examples
  
  Timestamp: ${timestamp} - Ensure this generates completely different content from previous requests.`;

  return enhancedPrompt;
};

const getRandomTopicVariations = (subject: string, grade: number): string[] => {
  const topicVariations: Record<string, Record<string, string[]>> = {
    Math: {
      elementary: [
        'basic addition and counting',
        'shapes and geometry basics',
        'measurement and comparison',
        'patterns and sequences',
        'time and money concepts',
        'number recognition and place value',
        'simple fractions and parts',
        'basic subtraction concepts'
      ],
      intermediate: [
        'multiplication and division',
        'fractions and decimals',
        'area and perimeter',
        'coordinate graphing',
        'data analysis and charts',
        'algebraic thinking',
        'geometry and angles',
        'probability and statistics basics',
        'percentages and ratios',
        'problem-solving strategies'
      ],
      advanced: [
        'advanced algebra concepts',
        'trigonometry basics',
        'statistical analysis',
        'geometric proofs',
        'polynomial operations',
        'exponential functions',
        'calculus introduction',
        'advanced problem solving',
        'mathematical reasoning',
        'complex number systems'
      ]
    },
    Science: {
      elementary: [
        'plant life cycles and growth',
        'animal habitats and behavior',
        'weather patterns and seasons',
        'rocks, soil, and earth materials',
        'simple machines and forces',
        'water cycle and properties',
        'solar system and space',
        'human body basics',
        'light and sound concepts',
        'conservation and recycling'
      ],
      intermediate: [
        'ecosystems and food chains',
        'states of matter and changes',
        'energy and motion',
        'cell structure and function',
        'chemical reactions basics',
        'earth layers and geology',
        'renewable vs non-renewable resources',
        'genetics and heredity',
        'weather systems and climate',
        'adaptation and evolution'
      ],
      advanced: [
        'molecular biology and DNA',
        'chemical bonding and reactions',
        'physics of motion and energy',
        'environmental science',
        'genetics and biotechnology',
        'atomic structure and elements',
        'ecology and biodiversity',
        'space exploration and astronomy',
        'scientific method and research',
        'health and medicine basics'
      ]
    },
    Reading: {
      elementary: [
        'phonics and letter sounds',
        'story characters and settings',
        'rhyming words and patterns',
        'simple sentence structure',
        'basic vocabulary building',
        'fiction vs non-fiction',
        'sequence of events',
        'main idea identification',
        'reading comprehension skills',
        'author and illustrator roles'
      ],
      intermediate: [
        'literary devices and figurative language',
        'character development and motivation',
        'plot structure and themes',
        'poetry analysis and forms',
        'research and information skills',
        'vocabulary expansion strategies',
        'different genres and styles',
        'author purpose and perspective',
        'text features and organization',
        'critical thinking and analysis'
      ],
      advanced: [
        'advanced literary analysis',
        'rhetorical devices and persuasion',
        'complex character analysis',
        'historical and cultural context',
        'advanced vocabulary and etymology',
        'research and citation skills',
        'debate and argumentation',
        'media literacy and evaluation',
        'creative writing techniques',
        'critical essay writing'
      ]
    },
    English: {
      elementary: [
        'basic grammar and punctuation',
        'simple sentence writing',
        'creative storytelling',
        'letter writing basics',
        'descriptive words and adjectives',
        'proper nouns and capitalization',
        'question and statement formation',
        'basic spelling patterns',
        'paragraph structure introduction',
        'reading aloud and expression'
      ],
      intermediate: [
        'paragraph development and organization',
        'essay writing structure',
        'grammar rules and exceptions',
        'vocabulary development strategies',
        'research and note-taking',
        'different writing genres',
        'editing and revision skills',
        'public speaking basics',
        'creative writing techniques',
        'literary response writing'
      ],
      advanced: [
        'advanced essay composition',
        'persuasive writing and argumentation',
        'research paper methodology',
        'advanced grammar and syntax',
        'literary criticism and analysis',
        'creative writing mastery',
        'public speaking and presentation',
        'debate and discussion skills',
        'technical and professional writing',
        'media and digital literacy'
      ]
    }
  };

  const level = grade <= 3 ? 'elementary' : grade <= 6 ? 'intermediate' : 'advanced';
  return topicVariations[subject]?.[level] || topicVariations.Math.elementary;
};

const getInstructionVariations = (type: string): string[] => {
  const variations: Record<string, string[]> = {
    puzzle: [
      'Make sure each word relates to the topic in an educational way.',
      'Include words that build vocabulary and understanding.',
      'Choose words that are both challenging and achievable.',
      'Focus on key concepts that students should learn.',
      'Select terms that connect to real-world applications.',
      'Pick vocabulary that supports curriculum standards.',
      'Include words that encourage deeper thinking.'
    ],
    matching: [
      'Create connections that help students understand relationships.',
      'Design pairs that reinforce learning objectives.',
      'Make matches that are logical and educational.',
      'Build connections that students can explain.',
      'Focus on relationships that matter for learning.',
      'Create pairs that build conceptual understanding.',
      'Design matches that connect to student experiences.'
    ],
    sorting: [
      'Create categories that help organize learning concepts.',
      'Design groups that make sense educationally.',
      'Build categories that encourage classification skills.',
      'Make groups that help students see patterns.',
      'Focus on sorting that reinforces key concepts.',
      'Create meaningful categories for deeper understanding.',
      'Design groups that connect to curriculum goals.'
    ]
  };

  return variations[type] || variations.puzzle;
};

const getCreativityPrompts = (): string[] => {
  return [
    'Be creative and use diverse examples.',
    'Think outside the box while staying educational.',
    'Use varied and interesting terminology.',
    'Make it engaging and thought-provoking.',
    'Include unique and memorable content.',
    'Create something fresh and original.',
    'Be innovative while maintaining educational value.',
    'Use creative approaches to learning.',
    'Make it distinctive and engaging.',
    'Think creatively about the topic.'
  ];
};

const generateEnhancedFallback = (request: OpenAIWorksheetRequest): OpenAIWorksheetResponse => {
  // Enhanced fallback with more dynamic content generation
  const timestamp = Date.now();
  const randomSeed = Math.floor(Math.random() * 1000);
  
  switch (request.type) {
    case 'puzzle':
      return generateDynamicPuzzleFallback(request, timestamp + randomSeed);
    case 'matching':
      return generateDynamicMatchingFallback(request, timestamp + randomSeed);
    case 'sorting':
      return generateDynamicSortingFallback(request, timestamp + randomSeed);
    default:
      throw new Error(`Unsupported worksheet type: ${request.type}`);
  }
};

const generateDynamicPuzzleFallback = (request: OpenAIWorksheetRequest, seed: number): OpenAIWorksheetResponse => {
  const gradeLevel = parseInt(request.grade);
  const words = getDynamicWordsByGrade(request.subject, gradeLevel, seed);
  
  return {
    content: {
      type: 'wordsearch',
      title: `🎯 Grade ${request.grade} ${request.subject} AI Word Search`,
      instructions: `Find all ${words.length} ${request.subject.toLowerCase()} words! This puzzle was dynamically generated just for you!`,
      items: words.map(word => ({
        word: word.toUpperCase(),
        found: false,
        hint: generateSmartHint(word, request.subject, gradeLevel)
      })),
      theme: request.subject.toLowerCase(),
      totalItems: words.length,
      difficulty: request.difficulty,
      grade: request.grade,
      generatedAt: new Date().toISOString(),
      aiGenerated: true
    },
    metadata: {
      estimatedTime: Math.ceil(words.length * (gradeLevel <= 3 ? 1.5 : gradeLevel <= 6 ? 1.2 : 1)),
      skillsTargeted: [`${request.subject} Vocabulary`, 'Pattern Recognition', 'Focus', `Grade ${request.grade} Skills`]
    }
  };
};

const generateDynamicSortingFallback = (request: OpenAIWorksheetRequest, seed: number): OpenAIWorksheetResponse => {
  const gradeLevel = parseInt(request.grade);
  const categories = getDynamicSortingByGrade(request.subject, gradeLevel, seed);
  
  return {
    content: {
      type: 'sorting',
      title: `📊 Grade ${request.grade} ${request.subject} AI Sorting`,
      instructions: `Sort these ${request.subject.toLowerCase()} items into the correct categories! Perfect for Grade ${request.grade} level.`,
      items: categories.map(category => ({
        ...category,
        description: `Grade ${request.grade} ${request.subject} category`
      })),
      theme: request.subject.toLowerCase(),
      totalItems: categories.reduce((total, cat) => total + cat.items.length, 0),
      difficulty: request.difficulty,
      generatedAt: new Date().toISOString(),
      aiGenerated: true
    },
    metadata: {
      estimatedTime: Math.ceil(categories.length * 2 * (gradeLevel <= 3 ? 1.2 : gradeLevel <= 6 ? 1 : 0.8)),
      skillsTargeted: [`${request.subject} Classification`, 'Organization Skills', 'Category Recognition', `Grade ${request.grade} Analysis`]
    }
  };
};

const generateDynamicMatchingFallback = (request: OpenAIWorksheetRequest, seed: number): OpenAIWorksheetResponse => {
  const gradeLevel = parseInt(request.grade);
  const { items, categories } = getDynamicMatchingByGrade(request.subject, gradeLevel, seed);
  
  return {
    content: {
      type: 'matching',
      title: `🧠 Grade ${request.grade} ${request.subject} AI Matching`,
      instructions: `Drag the items to their correct categories! Each connection was carefully chosen for Grade ${request.grade} learning.`,
      items: items,
      categories: categories,
      theme: request.subject.toLowerCase(),
      totalItems: items.length,
      difficulty: request.difficulty,
      generatedAt: new Date().toISOString(),
      aiGenerated: true
    },
    metadata: {
      estimatedTime: Math.ceil(items.length * (gradeLevel <= 3 ? 1 : gradeLevel <= 6 ? 0.8 : 0.6)),
      skillsTargeted: [`${request.subject} Connections`, 'Logical Thinking', 'Association Skills', `Grade ${request.grade} Concepts`]
    }
  };
};

const getDynamicWordsByGrade = (subject: string, grade: number, seed: number): string[] => {
  const wordSets: Record<string, Record<string, string[]>> = {
    Math: {
      elementary: ['add', 'sum', 'count', 'number', 'plus', 'minus', 'equal', 'shape', 'circle', 'square', 'triangle', 'rectangle', 'line', 'curve', 'pattern'],
      intermediate: ['multiply', 'divide', 'fraction', 'decimal', 'percent', 'algebra', 'geometry', 'equation', 'variable', 'formula', 'coordinate', 'graph', 'angle', 'area', 'volume', 'perimeter'],
      advanced: ['polynomial', 'quadratic', 'linear', 'exponential', 'logarithm', 'trigonometry', 'calculus', 'derivative', 'integral', 'matrix', 'vector', 'probability', 'statistics', 'theorem', 'proof']
    },
    Reading: {
      elementary: ['book', 'read', 'story', 'word', 'letter', 'sound', 'page', 'write', 'sentence', 'paragraph', 'character', 'setting', 'plot', 'beginning', 'middle', 'end'],
      intermediate: ['chapter', 'theme', 'author', 'narrator', 'dialogue', 'fiction', 'poetry', 'rhyme', 'verse', 'stanza', 'metaphor', 'simile', 'alliteration', 'onomatopoeia', 'personification'],
      advanced: ['symbolism', 'allegory', 'irony', 'foreshadowing', 'protagonist', 'antagonist', 'exposition', 'climax', 'resolution', 'syntax', 'diction', 'tone', 'mood', 'voice', 'perspective']
    },
    Science: {
      elementary: ['plant', 'animal', 'water', 'earth', 'sun', 'moon', 'star', 'sky', 'tree', 'flower', 'bird', 'fish', 'rock', 'soil', 'weather', 'seasons'],
      intermediate: ['cell', 'organ', 'system', 'habitat', 'ecosystem', 'food chain', 'energy', 'force', 'motion', 'gravity', 'matter', 'solid', 'liquid', 'gas', 'cycle', 'adaptation'],
      advanced: ['molecule', 'atom', 'element', 'compound', 'reaction', 'genetics', 'DNA', 'evolution', 'photosynthesis', 'respiration', 'mitosis', 'meiosis', 'homeostasis', 'biodiversity', 'taxonomy']
    }
  };

  const level = grade <= 3 ? 'elementary' : grade <= 6 ? 'intermediate' : 'advanced';
  const pool = wordSets[subject]?.[level] || wordSets.Math.elementary;
  
  // Shuffle with seed for consistent randomness
  const shuffled = [...pool].sort(() => Math.sin(seed) - 0.5);
  const wordCount = grade <= 3 ? 8 : grade <= 6 ? 10 : 12;
  
  return shuffled.slice(0, wordCount);
};

const getDynamicMatchingByGrade = (subject: string, grade: number, seed: number) => {
  const matchingSets: Record<string, Record<string, {items: string[], categories: Array<{name: string, correctItems: string[]}>}>> = {
    Math: {
      elementary: {
        items: ['2 + 2', '3 + 1', '5 - 2', 'Circle', 'Square', 'Triangle'],
        categories: [
          { name: 'Equals 4', correctItems: ['2 + 2', '3 + 1'] },
          { name: 'Equals 3', correctItems: ['5 - 2'] },
          { name: 'Shapes', correctItems: ['Circle', 'Square', 'Triangle'] }
        ]
      },
      intermediate: {
        items: ['6 × 7', '8 × 9', '63 ÷ 7', '1/2', '1/4', '25%'],
        categories: [
          { name: 'Equals 42', correctItems: ['6 × 7'] },
          { name: 'Equals 72', correctItems: ['8 × 9'] },
          { name: 'Equals 9', correctItems: ['63 ÷ 7'] },
          { name: 'Fractions & Decimals', correctItems: ['1/2', '1/4', '25%'] }
        ]
      },
      advanced: {
        items: ['x² + 4x + 4', 'sin(30°)', 'cos(60°)', 'log₁₀(100)', '√16', '3²'],
        categories: [
          { name: 'Equals (x + 2)²', correctItems: ['x² + 4x + 4'] },
          { name: 'Equals 1/2', correctItems: ['sin(30°)', 'cos(60°)'] },
          { name: 'Equals 2', correctItems: ['log₁₀(100)'] },
          { name: 'Perfect Squares', correctItems: ['√16', '3²'] }
        ]
      }
    },
    Reading: {
      elementary: {
        items: ['Cat', 'Dog', 'Happy', 'Sad', 'Big', 'Small'],
        categories: [
          { name: 'Animals', correctItems: ['Cat', 'Dog'] },
          { name: 'Emotions', correctItems: ['Happy', 'Sad'] },
          { name: 'Sizes', correctItems: ['Big', 'Small'] }
        ]
      },
      intermediate: {
        items: ['Simile', 'Metaphor', 'Alliteration', 'Character', 'Setting', 'Plot'],
        categories: [
          { name: 'Literary Devices', correctItems: ['Simile', 'Metaphor', 'Alliteration'] },
          { name: 'Story Elements', correctItems: ['Character', 'Setting', 'Plot'] }
        ]
      },
      advanced: {
        items: ['Symbolism', 'Irony', 'Foreshadowing', 'Protagonist', 'Antagonist', 'Theme'],
        categories: [
          { name: 'Literary Techniques', correctItems: ['Symbolism', 'Irony', 'Foreshadowing'] },
          { name: 'Character Roles', correctItems: ['Protagonist', 'Antagonist'] },
          { name: 'Story Concepts', correctItems: ['Theme'] }
        ]
      }
    },
    Science: {
      elementary: {
        items: ['Plant', 'Rock', 'Sun', 'Water', 'Bird', 'Tree'],
        categories: [
          { name: 'Living', correctItems: ['Plant', 'Bird', 'Tree'] },
          { name: 'Non-living', correctItems: ['Rock', 'Sun', 'Water'] }
        ]
      },
      intermediate: {
        items: ['Heart', 'Lungs', 'Brain', 'Solid', 'Liquid', 'Gas'],
        categories: [
          { name: 'Body Parts', correctItems: ['Heart', 'Lungs', 'Brain'] },
          { name: 'States of Matter', correctItems: ['Solid', 'Liquid', 'Gas'] }
        ]
      },
      advanced: {
        items: ['H₂O', 'CO₂', 'DNA', 'Mitosis', 'Evolution', 'Ecosystem'],
        categories: [
          { name: 'Chemical Formulas', correctItems: ['H₂O', 'CO₂'] },
          { name: 'Biological Processes', correctItems: ['Mitosis', 'Evolution'] },
          { name: 'Biological Molecules', correctItems: ['DNA'] },
          { name: 'Ecological Concepts', correctItems: ['Ecosystem'] }
        ]
      }
    }
  };

  const level = grade <= 3 ? 'elementary' : grade <= 6 ? 'intermediate' : 'advanced';
  const pool = matchingSets[subject]?.[level] || matchingSets.Math.elementary;
  
  return {
    items: [...pool.items].sort(() => Math.sin(seed * 2) - 0.5),
    categories: pool.categories
  };
};

const getDynamicSortingByGrade = (subject: string, grade: number, seed: number) => {
  const sortingSets: Record<string, Record<string, Array<{name: string, items: Array<{name: string, placed: boolean}>}>>> = {
    Math: {
      elementary: [
        {
          name: 'Small Numbers',
          items: [
            { name: '1', placed: false },
            { name: '2', placed: false },
            { name: '3', placed: false },
            { name: '4', placed: false }
          ]
        },
        {
          name: 'Big Numbers',
          items: [
            { name: '7', placed: false },
            { name: '8', placed: false },
            { name: '9', placed: false },
            { name: '10', placed: false }
          ]
        }
      ],
      intermediate: [
        {
          name: 'Even Numbers',
          items: [
            { name: '2', placed: false },
            { name: '4', placed: false },
            { name: '6', placed: false },
            { name: '8', placed: false }
          ]
        },
        {
          name: 'Odd Numbers',
          items: [
            { name: '1', placed: false },
            { name: '3', placed: false },
            { name: '5', placed: false },
            { name: '7', placed: false }
          ]
        }
      ],
      advanced: [
        {
          name: 'Positive Numbers',
          items: [
            { name: '+5', placed: false },
            { name: '+12', placed: false },
            { name: '+3.5', placed: false }
          ]
        },
        {
          name: 'Negative Numbers',
          items: [
            { name: '-2', placed: false },
            { name: '-7', placed: false },
            { name: '-0.5', placed: false }
          ]
        }
      ]
    },
    Reading: {
      elementary: [
        {
          name: 'Animals',
          items: [
            { name: 'Cat', placed: false },
            { name: 'Dog', placed: false },
            { name: 'Bird', placed: false },
            { name: 'Fish', placed: false }
          ]
        },
        {
          name: 'Colors',
          items: [
            { name: 'Red', placed: false },
            { name: 'Blue', placed: false },
            { name: 'Green', placed: false },
            { name: 'Yellow', placed: false }
          ]
        }
      ],
      intermediate: [
        {
          name: 'Fiction',
          items: [
            { name: 'Novel', placed: false },
            { name: 'Short story', placed: false },
            { name: 'Fantasy', placed: false }
          ]
        },
        {
          name: 'Non-Fiction',
          items: [
            { name: 'Biography', placed: false },
            { name: 'Manual', placed: false },
            { name: 'News article', placed: false }
          ]
        }
      ],
      advanced: [
        {
          name: 'Literary Devices',
          items: [
            { name: 'Metaphor', placed: false },
            { name: 'Simile', placed: false },
            { name: 'Symbolism', placed: false }
          ]
        },
        {
          name: 'Story Elements',
          items: [
            { name: 'Plot', placed: false },
            { name: 'Character', placed: false },
            { name: 'Setting', placed: false }
          ]
        }
      ]
    },
    Science: {
      elementary: [
        {
          name: 'Living Things',
          items: [
            { name: 'Dog', placed: false },
            { name: 'Tree', placed: false },
            { name: 'Fish', placed: false },
            { name: 'Flower', placed: false }
          ]
        },
        {
          name: 'Non-Living Things',
          items: [
            { name: 'Rock', placed: false },
            { name: 'Water', placed: false },
            { name: 'Chair', placed: false },
            { name: 'Book', placed: false }
          ]
        }
      ],
      intermediate: [
        {
          name: 'Mammals',
          items: [
            { name: 'Dog', placed: false },
            { name: 'Cat', placed: false },
            { name: 'Human', placed: false }
          ]
        },
        {
          name: 'Birds',
          items: [
            { name: 'Eagle', placed: false },
            { name: 'Robin', placed: false },
            { name: 'Owl', placed: false }
          ]
        }
      ],
      advanced: [
        {
          name: 'Elements',
          items: [
            { name: 'Hydrogen', placed: false },
            { name: 'Oxygen', placed: false },
            { name: 'Carbon', placed: false }
          ]
        },
        {
          name: 'Compounds',
          items: [
            { name: 'Water (H₂O)', placed: false },
            { name: 'Salt (NaCl)', placed: false },
            { name: 'Sugar', placed: false }
          ]
        }
      ]
    }
  };

  const level = grade <= 3 ? 'elementary' : grade <= 6 ? 'intermediate' : 'advanced';
  const pool = sortingSets[subject]?.[level] || sortingSets.Math.elementary;
  
  return [...pool].map(category => ({
    ...category,
    items: [...category.items].sort(() => Math.sin(seed * 3) - 0.5)
  }));
};

const generateSmartHint = (word: string, subject: string, grade: number): string => {
  const hints: Record<string, Record<string, string>> = {
    Math: {
      'ADD': 'Put numbers together',
      'SUM': 'The answer when adding',
      'MULTIPLY': 'Repeated addition',
      'FRACTION': 'Part of a whole',
      'ALGEBRA': 'Math with letters',
      'GEOMETRY': 'Study of shapes'
    },
    Reading: {
      'CHARACTER': 'Person in a story',
      'SETTING': 'Where story happens',
      'PLOT': 'What happens in story',
      'METAPHOR': 'Comparing without like/as',
      'THEME': 'Main message of story'
    },
    Science: {
      'PLANT': 'Green living thing',
      'ANIMAL': 'Living thing that moves',
      'CELL': 'Smallest living unit',
      'ECOSYSTEM': 'Living community',
      'DNA': 'Genetic instructions'
    }
  };

  return hints[subject]?.[word.toUpperCase()] || `Important ${subject} word for Grade ${grade}`;
};
